import dynamic from 'next/dynamic';
const CreateAccountPopup = dynamic(
  () => import('components/account/CreateAccountPopup')
);
const InstallPWAPopup = dynamic(() => import('components/InstallPWAPopup'));
const NewsletterSignupPrompt = dynamic(
  () => import('components/home-page/NewsletterSignupPrompt')
);
const ReferralPromptMessage = dynamic(
  () => import('components/referrals/ReferralPromptMessage')
);

import GenericModal from 'components/GenericModal';
import dayjs from 'dayjs';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import {
  EecProduct,
  trackEecImpression,
  trackEvent,
} from 'utils/analytics/analytics';
import { scrollToSection, sleep } from 'utils/common';

const PopupManager = observer(() => {
  const { currentUser, subscriptionStatusInfo } = useUserStore();
  const enterStore = useEnterStore();
  const enterStoreErrorPopupMessage = enterStore.errorPopupMessage;
  const setEnterStoreErrorPopupMessage = enterStore.setErrorPopupMessage;
  const router = useRouter();
  const { subscriberStatus, isFreemiumSubscriber } = subscriptionStatusInfo;

  const timerRef = useRef<any>(null);

  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  const [referralThreshold, setReferralThreshold] = useState(100);
  const [showInstallPWAPopup, setShowInstallPWAPopup] = useState(false);

  const [createAccountPopupAccountInfo, setCreateAccountPopupAccountInfo] =
    useState<{ userId: string; emailAddress: string } | null>(null);

  const showPopupIfNecessary = () => {
    if (
      !currentUser &&
      localStorage.getItem('previously_logged_in') !== 'true'
    ) {
      return;
    }
    if (shouldShowReferralPromptToUser()) {
      showReferralPromptToUser();
      return;
    }
    // Otherwise, show install PWA popup.
    setShowInstallPWAPopup(true);
  };

  const showCreateAccountPopupIfAppropriate = () => {
    const action = router?.query?.action;
    if (action !== 'upgrade-account' || currentUser !== null) return;
    const userId = router?.query?.userId as string;
    const emailAddress = router?.query?.emailAddress as string;

    if (userId && emailAddress) {
      setTimeout(
        () =>
          setCreateAccountPopupAccountInfo({
            userId,
            emailAddress,
          }),
        200
      );
    }
  };

  const showReferralPromptToUser = async () => {
    if (!subscriberStatus) return;

    /* ENG-1060 - Referrals temporarily disabled 
   
    // Show the popup and store the date in localStorage (so we know not to show the popup again for 14 days from now).
    localStorage.setItem(
      'date_last_shown_referral_prompt_popup',
      new Date().getTime().toString()
    );
    setShowReferralPrompt(true);
    trackEvent('Shown referral Popup');
    */
  };

  const shouldShowReferralPromptToUser = () => {
    // Only show if they haven't been shown this popup in the last 14 days.
    const lastShownDateString = localStorage.getItem(
      'date_last_shown_referral_prompt_popup'
    );
    if (lastShownDateString) {
      const lastShownDate = new Date(parseInt(lastShownDateString, 10));
      const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
      const daysSinceLastShown = Math.round(
        Math.abs((lastShownDate.getTime() - new Date().getTime()) / oneDay)
      );

      // Show xmas referral competition prompt more frequently (limited time only)
      if (
        dayjs().isAfter('2021-12-01') &&
        dayjs().isBefore('2021-12-18') &&
        daysSinceLastShown > 2
      ) {
        return true;
      }

      if (daysSinceLastShown < 14) return false;
    }
    return true;
  };

  useEffect(() => {
    (async () => {
      if (router?.query?.action === 'scroll-em') {
        sleep(700).then(() => scrollToSection('#entryMethodSelectionSection'));
        return;
      }

      showCreateAccountPopupIfAppropriate();
    })();
  }, [router?.query]);

  useEffect(() => {
    showPopupIfNecessary();
    trackEecImpression('home-page', EecProduct.LOTTERY);

    // Prevent updates on this component after it unmounts.
    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, []);
  return (
    <>
      {createAccountPopupAccountInfo && (
        <CreateAccountPopup
          userId={createAccountPopupAccountInfo.userId}
          emailAddress={createAccountPopupAccountInfo.emailAddress}
          onClose={() => setCreateAccountPopupAccountInfo(null)}
        />
      )}

      {showInstallPWAPopup && !createAccountPopupAccountInfo && (
        <InstallPWAPopup />
      )}

      {showReferralPrompt && !createAccountPopupAccountInfo && (
        <ReferralPromptMessage
          onClose={() => setShowReferralPrompt(false)}
          tokenReferralScheme={isFreemiumSubscriber}
        />
      )}
      {enterStoreErrorPopupMessage && (
        <GenericModal onClose={() => setEnterStoreErrorPopupMessage(null)}>
          {enterStoreErrorPopupMessage}
        </GenericModal>
      )}
    </>
  );
});

export default PopupManager;
